import { useState, useMemo } from "react";
import api from "../api/api";
import {
  Paper, Typography, Box, Button, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody,
  Tabs, Tab, Chip, CircularProgress, Alert, TablePagination,
  InputAdornment
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SearchIcon from "@mui/icons-material/Search";

const ROWS_OPTIONS = [10, 25, 50];
const fmt = (v) => v != null ? Number(v).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : "—";

const TIPOS = [
  { value: "", label: "All types" },
  { value: "general", label: "General" },
  { value: "puesto", label: "By Position" },
  { value: "opcional", label: "Optional" },
];

export default function ReporteHoras() {
  const [tab, setTab] = useState(0);
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [tipoCurso, setTipoCurso] = useState("");
  const [busqueda, setBusqueda] = useState("");

  const [datos, setDatos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pagina, setPagina] = useState(0);
  const [rowsPorPagina, setRowsPorPagina] = useState(10);

  const ENDPOINTS = ["por_curso", "por_empleado", "por_area"];

  const buscar = async () => {
    setLoading(true);
    setError("");
    setDatos([]);
    setBusqueda("");
    setPagina(0);
    try {
      const params = {};
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      if (tipoCurso)  params.tipo        = tipoCurso;
      const res = await api.get(`/reportes/horas/${ENDPOINTS[tab]}`, { params });
      setDatos(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Error loading the report.");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_, v) => {
    setTab(v);
    setDatos([]);
    setError("");
    setBusqueda("");
    setPagina(0);
  };

  const termino = busqueda.trim().toLowerCase();

  const datosFiltrados = useMemo(() => {
    if (!termino) return datos;
    return datos.filter(r => {
      if (tab === 0) return r.nombre_curso?.toLowerCase().includes(termino);
      if (tab === 1) return (
        r.nombre_empleado?.toLowerCase().includes(termino) ||
        r.puesto_texto?.toLowerCase().includes(termino) ||
        r.area_texto?.toLowerCase().includes(termino)
      );
      if (tab === 2) return r.area_texto?.toLowerCase().includes(termino);
      return true;
    });
  }, [datos, termino, tab]);

  const totalHoras = datosFiltrados.reduce((s, r) => s + (Number(r.total_horas) || 0), 0);
  const paginados = datosFiltrados.slice(pagina * rowsPorPagina, (pagina + 1) * rowsPorPagina);

  const hayFiltros = fechaDesde || fechaHasta || tipoCurso;

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <AssessmentIcon color="primary" />
        <Typography variant="h4">Man-hours Report</Typography>
      </Box>

      {/* Server-side filters */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end", mb: 3 }}>
        <TextField
          type="date" label="From" size="small"
          value={fechaDesde} onChange={e => setFechaDesde(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ width: 160 }}
        />
        <TextField
          type="date" label="To" size="small"
          value={fechaHasta} onChange={e => setFechaHasta(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ width: 160 }}
        />
        <TextField
          select label="Course type" size="small"
          value={tipoCurso} onChange={e => setTipoCurso(e.target.value)}
          sx={{ width: 180 }}
        >
          {TIPOS.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
        </TextField>
        <Button variant="contained" onClick={buscar} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : "Generate"}
        </Button>
        {hayFiltros && (
          <Button variant="text" onClick={() => {
            setFechaDesde(""); setFechaHasta(""); setTipoCurso("");
            setDatos([]); setBusqueda("");
          }}>
            Clear
          </Button>
        )}
      </Box>

      {/* Summary */}
      {datos.length > 0 && (
        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
          <Chip
            label={`${datosFiltrados.length}${termino ? ` of ${datos.length}` : ""} records`}
            variant="outlined"
          />
          <Chip
            label={`Total: ${fmt(totalHoras)} hours`}
            color="primary" variant="outlined"
            sx={{ fontWeight: 700 }}
          />
        </Box>
      )}

      {/* Tabs */}
      <Tabs value={tab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tab label="By Course" />
        <Tab label="By Employee" />
        <Tab label="By Area" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Table search — only visible when there is data */}
      {datos.length > 0 && (
        <TextField
          size="small"
          placeholder={
            tab === 0 ? "Search course…" :
            tab === 1 ? "Search employee, position, or area…" :
            "Search area…"
          }
          value={busqueda}
          onChange={e => { setBusqueda(e.target.value); setPagina(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
          sx={{ mb: 1.5, width: { xs: "100%", sm: 320 } }}
        />
      )}

      {/* By-Course table */}
      {tab === 0 && (
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: "#000" }}>
              <TableCell sx={{ color: "white" }}>Course</TableCell>
              <TableCell sx={{ color: "white" }}>Type</TableCell>
              <TableCell sx={{ color: "white" }} align="right">Training records</TableCell>
              <TableCell sx={{ color: "white" }} align="right">Total hours</TableCell>
              <TableCell sx={{ color: "white" }} align="right">Average hours</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginados.map((r, i) => (
              <TableRow key={i} hover>
                <TableCell>{r.nombre_curso}</TableCell>
                <TableCell>
                  <Chip label={r.tipo} size="small"
                    color={r.tipo === "general" ? "primary" : r.tipo === "puesto" ? "secondary" : "default"}
                  />
                </TableCell>
                <TableCell align="right">{r.total_registros}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(r.total_horas)}</TableCell>
                <TableCell align="right">{fmt(r.promedio_horas)}</TableCell>
              </TableRow>
            ))}
            {paginados.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  {datos.length === 0
                    ? "Press Generate to view the report"
                    : "No results for the search"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {/* By-Employee table */}
      {tab === 1 && (
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: "#000" }}>
              <TableCell sx={{ color: "white" }}>Employee</TableCell>
              <TableCell sx={{ color: "white" }}>Position</TableCell>
              <TableCell sx={{ color: "white" }}>Area</TableCell>
              <TableCell sx={{ color: "white" }} align="right">Courses taken</TableCell>
              <TableCell sx={{ color: "white" }} align="right">Total hours</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginados.map((r, i) => (
              <TableRow key={i} hover>
                <TableCell>{r.nombre_empleado}</TableCell>
                <TableCell>{r.puesto_texto || "—"}</TableCell>
                <TableCell>{r.area_texto || "—"}</TableCell>
                <TableCell align="right">{r.total_cursos}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(r.total_horas)}</TableCell>
              </TableRow>
            ))}
            {paginados.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  {datos.length === 0
                    ? "Press Generate to view the report"
                    : "No results for the search"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {/* By-Area table */}
      {tab === 2 && (
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: "#000" }}>
              <TableCell sx={{ color: "white" }}>Area</TableCell>
              <TableCell sx={{ color: "white" }} align="right">Employees</TableCell>
              <TableCell sx={{ color: "white" }} align="right">Training records</TableCell>
              <TableCell sx={{ color: "white" }} align="right">Total hours</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginados.map((r, i) => (
              <TableRow key={i} hover>
                <TableCell>{r.area_texto}</TableCell>
                <TableCell align="right">{r.total_empleados}</TableCell>
                <TableCell align="right">{r.total_registros}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(r.total_horas)}</TableCell>
              </TableRow>
            ))}
            {paginados.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  {datos.length === 0
                    ? "Press Generate to view the report"
                    : "No results for the search"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {datosFiltrados.length > 0 && (
        <TablePagination
          component="div"
          count={datosFiltrados.length}
          page={pagina}
          onPageChange={(_, p) => setPagina(p)}
          rowsPerPage={rowsPorPagina}
          onRowsPerPageChange={e => { setRowsPorPagina(parseInt(e.target.value)); setPagina(0); }}
          rowsPerPageOptions={ROWS_OPTIONS}
          labelRowsPerPage="Rows:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
          sx={{ borderTop: "1px solid", borderColor: "divider" }}
        />
      )}
    </Paper>
  );
}
